// #region Imports

import { Currency, Table } from '@mairistem/composants';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';

import { MODES_REGLEMENTS } from '@/constants';
import type { DataReglementType, FactureType } from '@/types';
import { estLigneEligible, formatMonetaire, getDetailProduit } from '@/utils';

// #endregion

// #region Types

interface ModalAffectationReglementTableBodyRowProps {
  indiceFacture: number;
  facture: FactureType;
  reglements: (DataReglementType & { code?: string })[];
  montantRegle: Record<string, unknown>[];
  shouldDisplayReste: boolean;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onChangeMontant: (
    identifiantFacture: number,
    identifiantLigne: number,
    identifiantReglement: string | number,
    value: string,
  ) => void;
}

// #endregion

const ModalAffectationReglementTableBodyRow = (
  props: ModalAffectationReglementTableBodyRowProps,
) => {
  const {
    indiceFacture,
    facture,
    reglements,
    montantRegle,
    shouldDisplayReste,
    onKeyPress,
    onChangeMontant,
  } = props;

  const { identifiant: identifiantFacture, numero, cycle, lignes } = facture;

  const montantTotal = React.useMemo(() => _.sumBy(lignes, 'montant'), [lignes]);

  // Libellés en doublon pour afficher le détail produit au survol
  const libellesEnDoublon = React.useMemo(() => {
    const counts = _.countBy(lignes, (l) => l?.produit?.libelle || '');
    return new Set(_.keys(_.pickBy(counts, (count) => count > 1)));
  }, [lignes]);

  let timeout;

  return (
    <Table.Row>
      <Table.Cell>{numero}</Table.Cell>
      <Table.Cell textAlign="center">{dayjs(cycle?.dateLimite)?.format('DD/MM/YYYY')}</Table.Cell>
      <Table.Cell style={{ padding: 0 }}>
        <Table
          style={{
            border: 'none',
            height: '100%',
            tableLayout: 'fixed',
          }}
          celled
        >
          <Table.Body>
            {_.map(lignes, (ligne) => {
              const libelle = ligne?.produit?.libelle || '';
              const title = libellesEnDoublon.has(libelle)
                ? getDetailProduit(ligne?.produit)
                : undefined;

              return (
                <Table.Row>
                  <Table.Cell
                    style={{
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      width: 200,
                    }}
                  >
                    <span title={title}>{libelle}</span>
                  </Table.Cell>
                  <Table.Cell
                    style={{
                      width: 90,
                    }}
                    textAlign="right"
                  >
                    {formatMonetaire(ligne.montant)}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </Table.Cell>
      <Table.Cell textAlign="right">
        <b>{formatMonetaire(montantTotal)}</b>
      </Table.Cell>
      <Table.Cell style={{ padding: 0 }}>
        <Table style={{ border: 'none', height: '100%' }} celled>
          <Table.Body>
            {_.map(lignes, (ligne) => (
              <Table.Row>
                <Table.Cell
                  style={{
                    width: 80,
                  }}
                  textAlign="right"
                >
                  {formatMonetaire(ligne.reste)}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Table.Cell>
      {_.map(reglements, (reglement, indiceReglement) => (
        <Table.Cell style={{ padding: 0, height: 0 }}>
          <Table className="nested" style={{ border: 'none', height: '100%' }} celled>
            <Table.Body>
              {_.map(lignes, (ligne, indice) => {
                const montantDansTableau = _.find(montantRegle, {
                  identifiantFacture,
                  identifiantLigne: ligne?.identifiant,
                  identifiantReglement: reglement?.identifiant,
                });

                const { montant: value, enterIndex, tabIndex } = montantDansTableau || {};

                // Un titre de paiement (CESU, chèque vacances) ne sert que les produits éligibles.
                const nonEligible = !estLigneEligible(ligne, reglement.code);

                return (
                  <Table.Row>
                    <Table.Cell
                      style={{
                        width: 80,
                      }}
                      textAlign="right"
                      {...(nonEligible && {
                        disabled: true,
                        title: `Produit non éligible au ${MODES_REGLEMENTS.ELIGIBILITE_TITRES[reglement.code].libelle}`,
                      })}
                      {...((value as number) > reglement.montant
                        ? { error: true }
                        : { warning: !nonEligible })}
                      onKeyDown={onKeyPress}
                    >
                      <Currency
                        id={`currency@${enterIndex}`}
                        data-testid={`currency-affectation-${enterIndex}`}
                        tabIndex={tabIndex}
                        {...(indice === 0 &&
                          indiceReglement === 0 &&
                          indiceFacture === 0 && { autoFocus: true })}
                        fluid
                        transparent
                        disabled={nonEligible}
                        valueMin={0}
                        valueMax={nonEligible ? 0 : Math.min(reglement.montant, ligne.reste)}
                        description=""
                        {...((nonEligible ||
                          value === 0 ||
                          (value === '' && shouldDisplayReste)) && {
                          placeholder: ' - ',
                        })}
                        value={value === 0 || nonEligible ? null : value}
                        onBlur={(e, v) =>
                          onChangeMontant(
                            identifiantFacture,
                            ligne?.identifiant,
                            reglement?.identifiant,
                            v,
                          )
                        }
                        onFocus={(e) => {
                          clearTimeout(timeout);
                          // Pour sélect tout le texte quand on utilise les flèches
                          // Si time > 0, si l'utilise appui 2 fois dans l'interval,
                          // ça bug complètement, et si pas de timeout,
                          // la flèche "bouge" (comportement naturel) dans l'input
                          // après la sélection (au key up je pense),
                          // ce qui donne l'impression que ça select rien
                          timeout = setTimeout(() => {
                            e.target.select();
                          }, 0);

                          if (value === 0) {
                            onChangeMontant(
                              identifiantFacture,
                              ligne?.identifiant,
                              reglement?.identifiant,
                              null,
                            );
                          }
                        }}
                      />
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </Table.Cell>
      ))}
      <Table.Cell style={{ padding: 0 }}>
        <Table style={{ border: 'none', height: '100%' }} celled>
          <Table.Body>
            {_.map(lignes, (ligne) => {
              const montantRegleLigne = _.filter(montantRegle, {
                identifiantFacture,
                identifiantLigne: ligne?.identifiant,
              });

              const value =
                (ligne?.reste || 0) - _.sumBy(montantRegleLigne, (m) => (m.montant as number) || 0);

              return (
                <Table.Row>
                  <Table.Cell
                    style={{
                      width: 80,
                    }}
                    textAlign="right"
                    error={parseFloat(value.toFixed(2)) < 0}
                  >
                    {formatMonetaire(value) === '-0,00' ? '0,00' : formatMonetaire(value)}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </Table.Cell>
    </Table.Row>
  );
};

export default ModalAffectationReglementTableBodyRow;
